import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, resolveComponent as _resolveComponent, createVNode as _createVNode, withCtx as _withCtx, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, unref as _unref, createElementBlock as _createElementBlock } from "vue"

const _hoisted_1 = {
  key: 0,
  class: "geotek-control"
}
const _hoisted_2 = {
  key: 2,
  class: "loading-container"
}

import { ref, watch, computed } from 'vue'
import type { EvCarProfileFull } from '@/models/evCarProfile'
import evCarsApi from '@/api/car-ev-data'
import { useEvCarsStore } from '@/store/ev-vehicles'
import { useAuthStore } from '@/store/auth'
import { ElIcon } from 'element-plus'
import { Loading } from '@element-plus/icons-vue'


export default /*@__PURE__*/_defineComponent({
  __name: 'GeotekControlSection',
  props: {
    vehicle: {}
  },
  setup(__props: any) {

const props = __props
const evCarsStore = useEvCarsStore()
const authStore = useAuthStore()

const canLockUnlock = computed(() => {
  const user = authStore.session?.user
  if (!user) return false
  return user.ev_role === 'admin' || user.ev_role === 'super_admin'
})

const response = ref('')
const error = ref('')
const loading = ref(false)

const vehicleData = ref<EvCarProfileFull | null>(null)
const vehicle = computed(() => vehicleData.value)

watch(
  () => props.vehicle,
  (newVal) => {
    vehicleData.value = newVal ? { ...newVal } : null
  },
  { immediate: true }
)

const clearMessages = () => {
  response.value = ''
  error.value = ''
}

const handleError = (e: unknown) => {
  if (e && typeof e === 'object' && 'response' in e) {
    const err = e as { response?: { data?: { message?: string } } }
    error.value = err.response?.data?.message || 'Unexpected error'
  } else {
    error.value = 'Unexpected error'
  }
}

const refreshCar = async () => {
  if (!props.vehicle?.id) return
  await evCarsStore.getEvCarDetails(props.vehicle.id)
  const updatedData = evCarsStore.evCarsDetails[props.vehicle.id]?.data
  if (updatedData) {
    vehicleData.value = { ...updatedData }
  }
}

const controlDoors = async (action: 'lock' | 'unlock') => {
  if (!canLockUnlock.value) return
  if (!vehicle.value?.id) return
  clearMessages()

  vehicle.value.status_doors = action === 'unlock'

  loading.value = true
  try {
    const id = vehicle.value.id
    const res = await evCarsApi.controlEvCarDoors(id, action)

    if (action === 'unlock') {
      await new Promise((r) => setTimeout(r, 1500))
      await evCarsApi.controlEvCarDoors(id, action)
    }

    response.value = res.data.message
  } catch (e) {
    vehicle.value.status_doors = false
    handleError(e)
  } finally {
    await refreshCar()
    loading.value = false
  }
}

const controlEngine = async (action: 'lock' | 'unlock') => {
  if (!canLockUnlock.value) return
  if (!props.vehicle?.id) return
  clearMessages()
  loading.value = true
  try {
    const res = await evCarsApi.controlEvCarEngine(props.vehicle.id, action)
    response.value = res.data.message
    await refreshCar()
  } catch (e) {
    handleError(e)
  } finally {
    loading.value = false
  }
}

const controlWindows = async (action: 'lock' | 'unlock') => {
  if (!canLockUnlock.value) return
  if (!props.vehicle?.id) return
  clearMessages()
  loading.value = true
  try {
    const res = await evCarsApi.controlEvCarWindows(props.vehicle.id, action)
    response.value = res.data.message
    await refreshCar()
  } catch (e) {
    handleError(e)
  } finally {
    loading.value = false
  }
}

return (_ctx: any,_cache: any) => {
  const _component_el_switch = _resolveComponent("el-switch")!
  const _component_el_descriptions_item = _resolveComponent("el-descriptions-item")!
  const _component_el_descriptions = _resolveComponent("el-descriptions")!
  const _component_el_alert = _resolveComponent("el-alert")!

  return (canLockUnlock.value)
    ? (_openBlock(), _createElementBlock("div", _hoisted_1, [
        _cache[4] || (_cache[4] = _createElementVNode("h3", null, "Geotek Control Panel", -1)),
        _createVNode(_component_el_descriptions, {
          border: "",
          column: 1,
          size: "large",
          class: "status-table"
        }, {
          default: _withCtx(() => [
            _createVNode(_component_el_descriptions_item, { label: "Doors control" }, {
              default: _withCtx(() => [
                _createVNode(_component_el_switch, {
                  "model-value": !!vehicle.value?.status_doors,
                  "active-text": "Unlocked",
                  "inactive-text": "Locked",
                  "active-value": true,
                  "inactive-value": false,
                  onChange: _cache[0] || (_cache[0] = (val) => controlDoors(val ? 'unlock' : 'lock')),
                  "active-color": '#67C23A',
                  "inactive-color": '#F56C6C',
                  size: "large",
                  disabled: loading.value || !canLockUnlock.value
                }, null, 8, ["model-value", "disabled"])
              ]),
              _: 1
            }),
            _createVNode(_component_el_descriptions_item, { label: "Engine control" }, {
              default: _withCtx(() => [
                _createVNode(_component_el_switch, {
                  "model-value": !!vehicle.value?.status_engine,
                  "active-text": "Unlocked",
                  "inactive-text": "Locked",
                  "active-value": true,
                  "inactive-value": false,
                  onChange: _cache[1] || (_cache[1] = (val) => controlEngine(val ? 'unlock' : 'lock')),
                  "active-color": '#67C23A',
                  "inactive-color": '#F56C6C',
                  size: "large",
                  disabled: loading.value || !canLockUnlock.value
                }, null, 8, ["model-value", "disabled"])
              ]),
              _: 1
            }),
            _createVNode(_component_el_descriptions_item, { label: "Windows control" }, {
              default: _withCtx(() => [
                _createVNode(_component_el_switch, {
                  "model-value": vehicle.value?.status_windows === false,
                  "active-text": "Closed",
                  "inactive-text": "Open",
                  "active-value": true,
                  "inactive-value": false,
                  onChange: _cache[2] || (_cache[2] = (val) => controlWindows(val ? 'lock' : 'unlock')),
                  "active-color": '#67C23A',
                  "inactive-color": '#F56C6C',
                  size: "large",
                  disabled: loading.value || !canLockUnlock.value
                }, null, 8, ["model-value", "disabled"])
              ]),
              _: 1
            })
          ]),
          _: 1
        }),
        (response.value)
          ? (_openBlock(), _createBlock(_component_el_alert, {
              key: 0,
              title: response.value,
              type: "success",
              "show-icon": "",
              class: "feedback"
            }, null, 8, ["title"]))
          : _createCommentVNode("", true),
        (error.value)
          ? (_openBlock(), _createBlock(_component_el_alert, {
              key: 1,
              title: error.value,
              type: "error",
              "show-icon": "",
              class: "feedback"
            }, null, 8, ["title"]))
          : _createCommentVNode("", true),
        (loading.value)
          ? (_openBlock(), _createElementBlock("div", _hoisted_2, [
              _createVNode(_unref(ElIcon), { class: "is-loading" }, {
                default: _withCtx(() => [
                  _createVNode(_unref(Loading))
                ]),
                _: 1
              }),
              _cache[3] || (_cache[3] = _createElementVNode("span", null, "Waiting for response from the car...", -1))
            ]))
          : _createCommentVNode("", true)
      ]))
    : _createCommentVNode("", true)
}
}

})